/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import Modal from '$components/Modal';
import Button from '$components/Button';
import { GradeType } from '$variables/types';
import hasAnyGrade from '$utils/hasAnyGrade';
import Step1 from '../Step1';
import Step2 from '../Step2';
import { ModalContent } from './UploadModal.styled';
import getStudentsNotInSpace from './utils/getStudentsNotInSpace';
import validateGrades from './utils/validateGrades';

const getStep2SubmitText = ({ hasGradesReleased, hasGrades }) => {
  if (hasGradesReleased) {
    return 'Override and release grades';
  }
  if (hasGrades) {
    return 'Override grades';
  }

  return 'Apply grades';
};

const UploadModal = ({
  onCloseModal,
  step,
  file,
  fileError,
  onChangeStep,
  onUploadGrades,
  isUploading,
  open,
  onDropFile,
  emailColumnIndex,
  gradeColumnIndex,
  onSelectEmailColumn,
  onSelectGradeColumn,
  grades,
  hasGradesReleased,
  gradeType,
}) => {
  const hasGrades = hasAnyGrade(grades);
  const {
    valid: validGrades,
    invalid: invalidGrades,
    duplicated: duplicateEmails,
  } = validateGrades({
    uploadedGrades: file ? file.data : [],
    currentGrades: grades,
    emailColumnIndex,
    gradeColumnIndex,
    gradeType,
  });

  const step2SubmitDisabled =
    typeof emailColumnIndex !== 'number' ||
    typeof gradeColumnIndex !== 'number' ||
    invalidGrades.length > 0 ||
    duplicateEmails.length > 0 ||
    validGrades.length === 0;

  const modalProps =
    step === 1
      ? {
          isDisabled: !file,
          headerText: '1 of 2: Upload CSV file',
          submitText: 'Next',
          handleSubmit: () => onChangeStep(2),
          footerDecoration: (
            <Button
              onClick={onCloseModal}
              label="Cancel"
              size="large"
              look="secondary"
            >
              Cancel
            </Button>
          ),
        }
      : {
          isDisabled: step2SubmitDisabled,
          headerText: '2 of 2: Choose data to use',
          submitText: getStep2SubmitText({ hasGrades, hasGradesReleased }),
          handleSubmit: onUploadGrades,
          isLoading: isUploading,
          footerDecoration: (
            <Button
              onClick={() => onChangeStep(1)}
              label="Previous"
              size="large"
              look="secondary"
            >
              Previous
            </Button>
          ),
        };

  return (
    <Modal open={open} handleClose={onCloseModal} {...modalProps}>
      <ModalContent>
        {step === 1 && (
          <Step1
            file={file}
            fileError={fileError}
            onFileDropComplete={onDropFile}
            hasGrades={hasGrades}
            hasGradesReleased={hasGradesReleased}
          />
        )}
        {step === 2 && (
          <Step2
            fileData={file.data}
            gradeType={gradeType}
            emailColumnIndex={emailColumnIndex}
            gradeColumnIndex={gradeColumnIndex}
            onSelectEmailColumn={onSelectEmailColumn}
            onSelectGradeColumn={onSelectGradeColumn}
            studentsNotInSpace={getStudentsNotInSpace({
              uploadedGrades: file.data,
              currentGrades: grades,
              emailColumnIndex,
            })}
            invalidGrades={invalidGrades}
            duplicateEmails={duplicateEmails}
            hasAnyValidGrades={validGrades.length > 0}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

UploadModal.propTypes = {
  step: PropTypes.number.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onChangeStep: PropTypes.func.isRequired,
  onUploadGrades: PropTypes.func.isRequired,
  onDropFile: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  open: PropTypes.bool,
  file: PropTypes.shape({
    name: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    errors: PropTypes.array.isRequired,
  }),
  fileError: PropTypes.string,
  onSelectEmailColumn: PropTypes.func.isRequired,
  onSelectGradeColumn: PropTypes.func.isRequired,
  emailColumnIndex: PropTypes.number,
  gradeColumnIndex: PropTypes.number,
  grades: PropTypes.arrayOf(GradeType).isRequired,
  hasGradesReleased: PropTypes.bool,
  gradeType: PropTypes.string.isRequired,
};

UploadModal.defaultProps = {
  isUploading: false,
  open: false,
  file: undefined,
  fileError: undefined,
  emailColumnIndex: undefined,
  gradeColumnIndex: undefined,
  hasGradesReleased: false,
};

export default UploadModal;
